import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function AdminAcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setToken(params.get("token") || "");
    setEmail(params.get("email") || "");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (!token) return toast.error("Invitation token missing or invalid");
    setLoading(true);
    try {
      await api.post("/auth/admin/accept-invite", { token, new_password: pw });
      toast.success("Password set. Please sign in.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err, "Could not accept invitation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <form onSubmit={submit}
          className="w-full max-w-md kcis-card p-8 sm:p-10 kcis-fade-up"
          data-testid="admin-accept-invite-form">
          <div className="h-12 w-12 rounded-2xl bg-purple-50 text-purple-900 flex items-center justify-center mb-5">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="kcis-overline text-purple-900">Practitioner access</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">Accept invitation</h1>
          <p className="mt-2 text-sm text-slate-600">
            {email
              ? <>You're accepting an invite for <span className="text-purple-900 font-medium">{email}</span>. Set a password to activate your account.</>
              : "Set a password to activate your KCIS practitioner account."}
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-sm text-slate-700">New password</Label>
              <Input data-testid="invite-new-password"
                type="password" autoComplete="new-password"
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="Minimum 8 characters" required
                className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Confirm password</Label>
              <Input data-testid="invite-confirm-password"
                type="password" autoComplete="new-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                placeholder="Re-enter password" required
                className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" />
            </div>
            <Button type="submit" disabled={loading || !token}
              className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12"
              data-testid="invite-accept-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate account"}
            </Button>
            {!token && (
              <p className="text-xs text-center text-red-600">
                This invitation link is invalid or has expired. Please request a fresh invite from your super admin.
              </p>
            )}
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
